import { API_V1_BASE } from "@/config/apiBase";
import { appOrigin } from "@/config/env";
import { mediaUrlFromApi } from "@/utils/mediaUrlFromApi";
import { cache } from "react";

function decodeHtmlEntities(str) {
  return String(str || "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#039;/g, "'")
    .replace(/&nbsp;/g, " ");
}

function stripHtml(html) {
  const stripped = String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return decodeHtmlEntities(stripped);
}

export function blogCanonicalPath(blog, fallbackKey = "") {
  const slug = blog?.slug != null ? String(blog.slug).trim() : "";
  if (slug) return `/blog/${encodeURIComponent(slug)}`;
  const id = blog?.id != null ? String(blog.id).trim() : "";
  if (id) return `/blog/${encodeURIComponent(id)}`;
  const key = fallbackKey != null ? String(fallbackKey).trim() : "";
  if (key) return `/blog/${encodeURIComponent(key)}`;
  return "/blog";
}

export function blogSeoFields(blog, { origin, pathKey } = {}) {
  const site = String(origin || appOrigin() || "https://www.tripsadora.com").replace(
    /\/+$/,
    "",
  );
  const title = decodeHtmlEntities(
    String(blog?.metaTitle || blog?.meta_title || "").trim() ||
    String(blog?.title || "").trim() ||
    "Blog | TripsAdora"
  );
  
  const h1Title = decodeHtmlEntities(
    String(blog?.title || "").trim() ||
    "Blog | TripsAdora"
  );

  const description = decodeHtmlEntities(
    String(blog?.metaDescription || blog?.meta_description || "").trim() ||
    String(blog?.excerpt || "").trim() ||
    stripHtml(blog?.content).slice(0, 160) ||
    "Read travel stories and tips on TripsAdora."
  );
  const path = blogCanonicalPath(blog, pathKey);
  const canonical = `${site}${path}`;
  const rawImage = blog?.image || blog?.image_url || "";
  const image = rawImage
    ? mediaUrlFromApi(String(rawImage).trim())
    : `${site}/images/default-blog.jpg`;
  const author = String(blog?.author || "TripsAdora").trim() || "TripsAdora";
  const authorUrl = blog?.socialLinks?.website || blog?.socialLinks?.linkedin || blog?.socialLinks?.twitter || `${site}/about`;
  
  const sameAsLinks = [];
  if (blog?.socialLinks) {
    const sl = blog.socialLinks;
    if (sl.website) sameAsLinks.push(sl.website);
    if (sl.linkedin) sameAsLinks.push(sl.linkedin);
    if (sl.twitter) sameAsLinks.push(sl.twitter);
    if (sl.instagram) sameAsLinks.push(sl.instagram);
    if (sl.facebook || sl.fb) sameAsLinks.push(sl.facebook || sl.fb);
  }

  const publishedAt = blog?.publishedAt || blog?.published_at || blog?.date || null;
  const updatedAt = blog?.updatedAt || blog?.updated_at || publishedAt;

  let schemas = [];

  // Define @id values to interconnect the graph
  const orgId = `${site}/#organization`;
  const websiteId = `${site}/#website`;
  const webPageId = `${canonical}/#webpage`;
  const imageId = `${canonical}/#primaryimage`;
  const articleId = `${canonical}/#article`; // Re-using as BlogPosting
  const breadcrumbId = `${canonical}/#breadcrumb`;

  const organizationSchema = {
    "@type": "Organization",
    "@id": orgId,
    name: "TripsAdora",
    url: site,
    logo: {
      "@type": "ImageObject",
      "@id": `${site}/#logo`,
      url: `${site}/images/logo1.png`,
    }
  };

  const websiteSchema = {
    "@type": "WebSite",
    "@id": websiteId,
    url: site,
    name: "TripsAdora",
    publisher: { "@id": orgId }
  };

  const imageSchema = {
    "@type": "ImageObject",
    "@id": imageId,
    url: image,
    contentUrl: image,
    caption: title,
    inLanguage: "en"
  };

  const breadcrumbSchema = {
    "@type": "BreadcrumbList",
    "@id": breadcrumbId,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: site
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: `${site}/blog`
      },
      {
        "@type": "ListItem",
        position: 3,
        name: h1Title,
        item: canonical
      }
    ]
  };

  const webPageSchema = {
    "@type": "WebPage",
    "@id": webPageId,
    url: canonical,
    name: title,
    description: description,
    isPartOf: { "@id": websiteId },
    about: { "@id": orgId },
    primaryImageOfPage: { "@id": imageId },
    inLanguage: "en",
    breadcrumb: { "@id": breadcrumbId }
  };

  const touristAttractionSchema = {
    "@type": "TouristAttraction",
    "@id": `${canonical}/#touristAttraction`,
    name: title,
    description: description,
    image: { "@id": imageId },
    url: canonical
  };

  const blogPostingSchema = {
    "@type": "BlogPosting",
    "@id": articleId,
    headline: title,
    description: description,
    image: { "@id": imageId },
    author: { 
      "@type": "Person", 
      name: author, 
      url: authorUrl,
      ...(sameAsLinks.length > 0 ? { sameAs: sameAsLinks } : {})
    },
    publisher: { "@id": orgId },
    mainEntityOfPage: { "@id": webPageId },
    datePublished: publishedAt || undefined,
    dateModified: updatedAt || publishedAt || undefined,
    url: canonical,
    inLanguage: "en",
    about: { "@id": `${canonical}/#touristAttraction` }
  };

  let faqSchema = null;
  if (Array.isArray(blog?.faqs) && blog.faqs.length > 0) {
    faqSchema = {
      "@type": "FAQPage",
      "@id": `${canonical}/#faq`,
      mainEntityOfPage: { "@id": webPageId },
      mainEntity: blog.faqs.map((faq) => ({
        "@type": "Question",
        name: String(faq.q).trim(),
        acceptedAnswer: {
          "@type": "Answer",
          text: String(faq.a).trim(),
        },
      })),
    };
  }

  schemas = [
    imageSchema,
    breadcrumbSchema,
    webPageSchema,
    touristAttractionSchema,
    blogPostingSchema
  ];

  if (faqSchema) {
    schemas.push(faqSchema);
  }

  // Add @context to all schemas
  schemas = schemas.map(s => ({
    "@context": "https://schema.org",
    ...s
  }));

  return {
    title,
    description,
    canonical,
    image,
    author,
    publishedAt,
    updatedAt,
    category: blog?.category || "Travel Tips",
    tags: Array.isArray(blog?.tags) ? blog.tags : typeof blog?.tags === 'string' ? blog.tags.split(',').map(t => t.trim()) : [],
    schemas
  };
}

/** Server-side public blog fetch for metadata (no cache-bust query). */
export const fetchPublicBlogForSeo = cache(async (pathKey) => {
  const key = pathKey != null ? String(pathKey).trim() : "";
  if (!key || !API_V1_BASE) return null;

  const tryUrls = [];
  if (key.includes(":")) {
    tryUrls.push(`${API_V1_BASE}/customer/blogs/${encodeURIComponent(key)}`);
  } else if (/^\d+$/.test(key)) {
    // numeric alone is ambiguous; skip id-only without writer
  } else {
    tryUrls.push(`${API_V1_BASE}/customer/blogs/slug/${encodeURIComponent(key)}`);
  }

  for (const url of tryUrls) {
    try {
      const res = await fetch(url, {
        method: "GET",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) continue;
      const data = await res.json();
      const blog = data?.blog != null ? data.blog : data;
      if (blog && (blog.title || blog.slug || blog.id)) return blog;
    } catch {
      // try next
    }
  }
  return null;
});
